import React from 'react';
import { X, Phone, MessageCircle, Tag } from 'lucide-react';
import { Conversation } from '../types';
import { format, parseISO } from 'date-fns';

interface CustomerInfoProps {
  conversation: Conversation;
  onClose: () => void;
}

const CustomerInfo: React.FC<CustomerInfoProps> = ({ conversation, onClose }) => {
  return (
    <div className="w-80 bg-white border-l border-gray-200 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Customer Information</h3>
        <button
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600 rounded"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Customer Details */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Profile */}
        <div className="text-center">
          <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-xl font-bold text-white">
              {conversation.customer_name?.charAt(0) || 'C'}
            </span>
          </div>
          <h4 className="text-lg font-medium text-gray-900">
            {conversation.customer_name || 'Unknown Customer'}
          </h4>
          <p className="text-gray-500">{conversation.customer_phone}</p>
        </div>

        {/* Contact Information */}
        <div className="space-y-3">
          <h5 className="text-sm font-medium text-gray-900 uppercase tracking-wide">
            Contact Information
          </h5>
          
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <Phone className="w-4 h-4 text-gray-500" />
            <div>
              <p className="text-sm font-medium text-gray-900">{conversation.customer_phone}</p>
              <p className="text-xs text-gray-500">Phone Number</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <MessageCircle className="w-4 h-4 text-gray-500" />
            <div>
              <p className="text-sm font-medium text-gray-900 capitalize">{conversation.channel}</p>
              <p className="text-xs text-gray-500">Channel</p>
            </div>
          </div>
        </div>

        {/* Conversation Details */}
        <div className="space-y-3">
          <h5 className="text-sm font-medium text-gray-900 uppercase tracking-wide">
            Conversation Details
          </h5>
          
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Status</span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                conversation.status === 'active' ? 'bg-green-100 text-green-800' :
                conversation.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                conversation.status === 'ai-handled' ? 'bg-blue-100 text-blue-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {conversation.status}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Created</span>
              <span className="text-sm text-gray-900">
                {format(parseISO(conversation.created_at), 'MMM dd, yyyy HH:mm')}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Last Updated</span>
              <span className="text-sm text-gray-900">
                {format(parseISO(conversation.updated_at), 'MMM dd, yyyy HH:mm')}
              </span>
            </div>

            {conversation.assigned_agent && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Assigned Agent</span>
                <span className="text-sm text-gray-900">{conversation.assigned_agent}</span>
              </div>
            )}
          </div>
        </div>

        {/* Tags */}
        <div className="space-y-3">
          <h5 className="text-sm font-medium text-gray-900 uppercase tracking-wide">
            Tags
          </h5>
          
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
              <Tag className="w-3 h-3" />
              New Customer
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
              <Tag className="w-3 h-3" />
              Support
            </span>
          </div>
        </div>

        {/* Notes */}
        <div className="space-y-3">
          <h5 className="text-sm font-medium text-gray-900 uppercase tracking-wide">
            Internal Notes
          </h5>
          
          <div className="bg-gray-50 rounded-lg p-3">
            <textarea
              placeholder="Add internal notes about this customer..."
              className="w-full bg-transparent border-none text-sm text-gray-700 placeholder-gray-500 resize-none focus:ring-0"
              rows={3}
            />
          </div>
        </div>

        {/* Previous Conversations */}
        <div className="space-y-3">
          <h5 className="text-sm font-medium text-gray-900 uppercase tracking-wide">
            Previous Conversations
          </h5>
          
          <div className="text-sm text-gray-500">
            No previous conversations found
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="p-4 border-t border-gray-200 space-y-2">
        <button className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
          Call Customer
        </button>
        <button className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
          View Full Profile
        </button>
      </div>
    </div>
  );
};

export default CustomerInfo;
